"use client";

import { motion, useMotionValue, useTransform } from "framer-motion";
import { MouseEvent } from "react";
import Magnetic from "./ui/Magnetic";

const philosophies = [
  {
    title: "Performance First",
    description: "Obsessed with latency, bundle size, and rendering speed. Aiming for 100/100 Lighthouse scores, minimal TTFB, and zero layout shifts (CLS).",
    icon: (
      <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    color: "group-hover:border-amber-500/30",
    bg: "bg-amber-500/5",
  },
  {
    title: "Security by Design",
    description: "Ensuring secure authentication, sanitizing inputs, encrypting sensitive fields, and enforcing Role-Based Access Controls (RBAC) at the network border.",
    icon: (
      <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    color: "group-hover:border-emerald-500/30",
    bg: "bg-emerald-500/5",
  },
  {
    title: "Developer Experience (DX)",
    description: "Creating highly readable codebases, comprehensive documentation, strict TypeScript configurations, clean git flows, and robust local developer tooling.",
    icon: (
      <svg className="w-6 h-6 text-indigo-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    color: "group-hover:border-indigo-500/30",
    bg: "bg-indigo-500/5",
  },
  {
    title: "Accessibility (a11y)",
    description: "Ensuring keyboard navigation support, high contrast ratios, screen reader accessibility with ARIA attributes, and native support for reduced motion.",
    icon: (
      <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    ),
    color: "group-hover:border-purple-500/30",
    bg: "bg-purple-500/5",
  },
  {
    title: "Horizontal Scalability",
    description: "Architecting stateless services, decoupling processing logic with queues, designing database indexes, and utilizing reverse proxy caching.",
    icon: (
      <svg className="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
    color: "group-hover:border-cyan-500/30",
    bg: "bg-cyan-500/5",
  },
  {
    title: "Clean Architecture",
    description: "Enforcing clear separation of concerns, SOLID principles, modular components, and dependency isolation to ensure long-term code maintainability.",
    icon: (
      <svg className="w-6 h-6 text-pink-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
      </svg>
    ),
    color: "group-hover:border-pink-500/30",
    bg: "bg-pink-500/5",
  },
  {
    title: "Automation (CI/CD)",
    description: "Eliminating manual steps with end-to-end delivery pipelines. Automating code linting, tests execution, image tagging, and blue-green deployments.",
    icon: (
      <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      </svg>
    ),
    color: "group-hover:border-blue-500/30",
    bg: "bg-blue-500/5",
  },
  {
    title: "Observability",
    description: "Instrumenting systems with tracing, structured error logging, server health alerts, metrics dashboards, and performance profiling.",
    icon: (
      <svg className="w-6 h-6 text-rose-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    color: "group-hover:border-rose-500/30",
    bg: "bg-rose-500/5",
  },
];

function Card({ phil }: { phil: typeof philosophies[0] }) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function handleMouseMove({ currentTarget, clientX, clientY }: MouseEvent) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <motion.div
      onMouseMove={handleMouseMove}
      className={`group relative overflow-hidden rounded-3xl border border-white/5 bg-gradient-to-br from-white/[0.03] to-transparent p-8 transition-colors duration-500 ${phil.color}`}
    >
      {/* Mouse hover glow effect */}
      <motion.div
        className="pointer-events-none absolute -inset-px rounded-3xl opacity-0 transition duration-300 group-hover:opacity-100"
        style={{
          background: useTransform(
            [mouseX, mouseY],
            ([x, y]) => `radial-gradient(350px circle at ${x}px ${y}px, rgba(99, 102, 241, 0.1), transparent 85%)`
          ),
        }}
      />
      <div className="relative z-10">
        <Magnetic>
          <div className={`w-14 h-14 rounded-2xl ${phil.bg} flex items-center justify-center mb-6`}>
            {phil.icon}
          </div>
        </Magnetic>
        <h3 className="text-xl font-bold text-white mb-3 font-[var(--font-poppins)] tracking-tight">
          {phil.title}
        </h3>
        <p className="text-gray-400 text-sm leading-relaxed font-medium">
          {phil.description}
        </p>
      </div>
    </motion.div>
  );
}

export default function Philosophy() {
  return (
    <section id="philosophy" className="section-pad relative z-10 bg-black/20">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="text-xs font-bold tracking-[0.4em] text-indigo-400 uppercase mb-6 block">Core Mindset</span>
          <h2 className="text-4xl md:text-5xl lg:text-7xl font-black text-white mb-6 font-[var(--font-poppins)] tracking-tighter">
            Engineering <span className="gradient-text">Philosophy</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg font-medium">
            Architectural rules and core engineering values that shape every line of code I deploy.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {philosophies.map((phil) => (
            <Card key={phil.title} phil={phil} />
          ))}
        </div>
      </div>
    </section>
  );
}
